import type { WhitelistItem } from "@gearbox-protocol/cli-utils";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorBox,
  NetworkLogo,
  ScrollArea,
  Spinner,
  WarnBox,
} from "@gearbox-protocol/deploy-tools-ui";
import { api } from "api";
import type { FC } from "react";
import { useAsyncFn } from "react-use";
import useAllClosedAccounts from "./useAllClosedAccounts";

interface WhitelistKeepOpenDialogProps {
  whitelist: WhitelistItem[];
  onClose: () => void;
}

const WhitelistKeepOpenDialog: FC<WhitelistKeepOpenDialogProps> = props => {
  const { whitelist, onClose } = props;

  const {
    loading: accountsLoading,
    value: accountsToDelete,
    error: accountsError,
  } = useAllClosedAccounts(whitelist);

  const [{ loading, error }, submit] = useAsyncFn(async () => {
    await api.deleteWhitelist("liquidator", accountsToDelete ?? []);
    onClose();
    window.location.reload();
  }, [accountsToDelete, onClose]);

  return (
    <Dialog modal open={true} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Keep only open accounts</DialogTitle>
          <DialogDescription>
            {`Please confirm deletion of following ${accountsToDelete?.length ?? ""}/${whitelist.length} closed/zero-debt accounts:`}
          </DialogDescription>
        </DialogHeader>

        {!!accountsError && <ErrorBox error={accountsError} />}
        {!!error && <ErrorBox error={error} />}
        {accountsLoading && <Spinner />}
        {accountsToDelete?.length !== 0 ? (
          <ScrollArea className="h-72 w-full rounded-md border">
            <div className="flex flex-col gap-2">
              {accountsToDelete?.map(f => (
                <div
                  key={`${f.network}-${f.address}`}
                  className="flex flex-row gap-2 items-start"
                >
                  <NetworkLogo network={f.network} className="mt-1" />
                  <div className="flex flex-col">
                    <pre>{f.address}</pre>
                    <pre className="text-sm text-gray-500">{f.reason}</pre>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <WarnBox>No closed accounts found in whitelist</WarnBox>
        )}

        <DialogFooter>
          <Button
            type="submit"
            variant="destructive"
            disabled={loading || accountsLoading || !accountsToDelete?.length}
            loading={loading}
            onClick={submit}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhitelistKeepOpenDialog;
